import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Mail, Trash2, UserCog, KeyRound, ShieldCheck, Users as UsersIcon, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { IssueContractDialog } from "@/components/admin/IssueContractDialog";

const ROLES = [
  { value: "client", label: "Client" },
  { value: "affiliate", label: "Affiliate" },
  { value: "publisher", label: "Publisher" },
  { value: "admin", label: "Admin" },
];

const roleBadge = (role) => {
  const map = {
    admin: "bg-slate-900 text-white border-slate-900",
    affiliate: "bg-blue-50 text-blue-700 border-blue-200",
    publisher: "bg-violet-50 text-violet-700 border-violet-200",
    client: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return map[role] || map.client;
};

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "client", company: "" });
  const [busy, setBusy] = useState(false);
  // iter90n — Issue contract shortcut
  const [contractUser, setContractUser] = useState(null);

  const refresh = () => api.get("/admin/users").then(r => setRows(r.data || []));
  useEffect(() => { refresh(); }, []);

  const filtered = rows.filter(u => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (u.email || "").toLowerCase().includes(q)
      || (u.name || "").toLowerCase().includes(q)
      || (u.company || "").toLowerCase().includes(q);
  });

  const submitInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users/invite", inviteForm);
      toast.success(`Invite emailed to ${inviteForm.email}`);
      setInviteOpen(false);
      setInviteForm({ email: "", name: "", role: "client", company: "" });
      refresh();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 401) {
        msg = "Your session expired — please sign in again.";
        setTimeout(() => { window.location.href = "/login?next=/admin/users"; }, 1500);
      } else if (status === 403) {
        msg = "You don't have permission to invite users.";
      } else if (status === 409) {
        msg = detail || "A user with this email already exists.";
      } else if (status === 422 && Array.isArray(detail)) {
        msg = detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
      } else if (typeof detail === "string" && detail.length) {
        msg = detail;
      } else if (!err?.response) {
        msg = "Network error — couldn't reach the server.";
      } else {
        msg = `Invite failed (${status || "unknown error"})`;
      }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const changeRole = async (u, newRole) => {
    if (u.role === newRole) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { role: newRole });
      toast.success(`${u.email} is now ${newRole}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change role"); }
  };

  const sendReset = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/send-password-reset`);
      toast.success(`Password reset emailed to ${u.email}`);
    } catch (e) { toast.error("Could not send reset"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.email} deleted`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Users"
        subtitle={`${rows.length} total identities — clients, admins, affiliates, publishers.`}
        actions={
          <Button className="rounded-full" onClick={() => setInviteOpen(true)} data-testid="admin-invite-user">
            <Plus className="size-4" /> Invite user
          </Button>
        }
      />

      <div className="flex items-center justify-between gap-3">
        <Input placeholder="Search name, email or company…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-sm" data-testid="users-search" />
        <div className="text-xs text-slate-500">{filtered.length} of {rows.length}</div>
      </div>

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-12 text-center" data-testid="users-empty">
              <div className="inline-grid place-items-center size-12 rounded-xl bg-slate-50 mb-4"><UsersIcon className="size-5 text-slate-400" /></div>
              <h3 className="text-base font-semibold text-slate-900">No users match "{filter}"</h3>
              <p className="mt-1 text-sm text-slate-500">Try a different search or invite a new user.</p>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60"><tr className="text-xs uppercase tracking-widest text-slate-500 text-left">
                <th className="py-3 px-4 font-medium">Name</th><th className="font-medium">Email</th><th className="font-medium">Company</th><th className="font-medium">Role</th><th className="font-medium">Created</th><th className="font-medium text-right pr-4 w-12"></th>
              </tr></thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`user-row-${u.id.slice(0,8)}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2.5">
                        <Avatar className="size-7"><AvatarFallback className="bg-slate-900 text-white text-[10px]">{(u.name || "??").split(" ").map(s => s[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                        <span className="font-medium text-slate-900">{u.name}{u.id === me?.id && <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-700">you</span>}</span>
                      </div>
                    </td>
                    <td className="text-slate-700 font-mono text-xs">{u.email}</td>
                    <td className="text-slate-700">{u.company || "—"}</td>
                    <td>
                      <span className={`text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${roleBadge(u.role)}`}>{u.role}</span>
                    </td>
                    <td className="text-xs font-mono text-slate-500">{new Date(u.created_at).toLocaleDateString("en-GB")}</td>
                    <td className="text-right pr-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" data-testid={`user-menu-${u.id.slice(0,8)}`}>
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-slate-500">Change role</div>
                          {ROLES.map((r) => (
                            <DropdownMenuItem key={r.value} disabled={u.role === r.value} onSelect={() => changeRole(u, r.value)} data-testid={`user-role-${r.value}-${u.id.slice(0,8)}`}>
                              <UserCog className="size-3.5" /> {r.label} {u.role === r.value && <span className="ml-auto text-[10px] text-slate-400">current</span>}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => setContractUser(u)} data-testid={`user-issue-contract-${u.id.slice(0,8)}`}>
                            <FileText className="size-3.5" /> Issue contract…
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => sendReset(u)} data-testid={`user-reset-${u.id.slice(0,8)}`}>
                            <KeyRound className="size-3.5" /> Send password reset
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-rose-600 focus:text-rose-700" onSelect={() => deleteUser(u)} disabled={u.id === me?.id} data-testid={`user-delete-${u.id.slice(0,8)}`}>
                            <Trash2 className="size-3.5" /> Delete user
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite a new user</DialogTitle>
            <DialogDescription>They'll receive an email with a single-use link to set their own password (valid 48 hours).</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInvite} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="inv-name">Full name</Label>
                <Input id="inv-name" required minLength={2} value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} data-testid="invite-name" />
              </div>
              <div>
                <Label htmlFor="inv-company">Company</Label>
                <Input id="inv-company" value={inviteForm.company} onChange={(e) => setInviteForm({ ...inviteForm, company: e.target.value })} data-testid="invite-company" />
              </div>
            </div>
            <div>
              <Label htmlFor="inv-email">Email</Label>
              <Input id="inv-email" type="email" required value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} data-testid="invite-email" />
            </div>
            <div>
              <Label>Role</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {ROLES.map((r) => (
                  <button type="button" key={r.value} onClick={() => setInviteForm({ ...inviteForm, role: r.value })} className={`rounded-md border px-3 py-2 text-sm ${inviteForm.role === r.value ? "border-[#0055FF] bg-[#0055FF]/[0.04] text-slate-900" : "border-slate-200 text-slate-600"}`} data-testid={`invite-role-${r.value}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-full" data-testid="invite-submit">
                {busy ? "Sending…" : <><Mail className="size-4" /> Send invite</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter90n — Manual contract issuance shortcut */}
      <IssueContractDialog
        open={!!contractUser}
        onOpenChange={(o) => !o && setContractUser(null)}
        onIssued={refresh}
        initialUser={contractUser}
      />
    </div>
  );
}
